import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePreviousDistinct } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup/ButtonGroup';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useUnguardedClientPresentationDesignerContext } from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.context';
import { zIndexes } from '@/styles/zIndexes';

import {
  SystemPromptOption,
  useSlideEditorPromptContext,
} from './SlideEditor.promptContext';
import { useSlideEditorToolbarContext } from './SlideEditor.toolbar';

const EMPTY_OPTION_VALUE = '';
const CUSTOM_OPTION_VALUE = '__custom__';

/**
 * Find the system prompt whose `prompt` text matches the persisted prompt
 * exactly. Matching by text (rather than by stored key) makes the user's
 * selection drift: if the backend later updates the prompt text, the
 * persisted slide no longer matches and falls through to "Custom" instead of
 * silently swapping content beneath the user.
 */
function findMatchingSystemPrompt(
  systemPrompts: SystemPromptOption[],
  prompt: string
): SystemPromptOption | undefined {
  return systemPrompts.find((p) => p.prompt === prompt);
}

type RegenerateTab = 'refine' | 'modify';

const REGENERATE_TAB_OPTIONS = [
  { display: 'Refine output', value: 'refine' as const },
  { display: 'Modify prompt', value: 'modify' as const },
];

/**
 * The form content for the prompt popover. Manages its own selection/text state
 * and reads the toolbar context to detect open transitions and close on apply.
 *
 * Designed to be rendered inside a SlideEditorToolbarPopover with
 * dropdownKey="prompt".
 */
export function PromptPopoverContent() {
  const promptContext = useSlideEditorPromptContext();
  const designerCtx = useUnguardedClientPresentationDesignerContext();
  const { activeDropdown, setActiveDropdown } = useSlideEditorToolbarContext();

  const isOpen = activeDropdown === 'prompt';
  const prevIsOpen = usePreviousDistinct(isOpen);

  // Regenerate flow: in non-template (presentation) editing, the slide already
  // has an applied prompt — the user is iterating on prior output rather than
  // generating from scratch.
  const isRegenerateMode =
    !!promptContext &&
    !promptContext.isTemplate &&
    !!promptContext.customPrompt;

  // Generate-flow state (preset dropdown + custom text).
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [customText, setCustomText] = useState('');

  // Regenerate-flow state. Per-tab text is kept separately so switching tabs
  // preserves what the user has typed in each one.
  const [activeTab, setActiveTab] = useState<RegenerateTab>('refine');
  const [refineText, setRefineText] = useState('');
  const [modifyText, setModifyText] = useState('');

  const systemPrompts = useMemo(
    () => promptContext?.systemPrompts ?? [],
    [promptContext?.systemPrompts]
  );

  // Read systemPrompts via ref inside the init effect: a late-arriving query
  // result must not be in the dep array, since `prevIsOpen` (from
  // usePreviousDistinct) stays `false` for the entire time the popover is
  // open, so re-firing the effect would clobber in-progress user edits.
  const systemPromptsRef = useRef(systemPrompts);
  systemPromptsRef.current = systemPrompts;

  // Initialize selection state whenever the popover opens, regardless of whether
  // it was opened by a button click or externally by the toolbar capture handler.
  useEffect(() => {
    // Only run when transitioning from closed → open
    if (!isOpen || prevIsOpen) return;
    const currentPrompt = promptContext?.customPrompt ?? '';
    if (isRegenerateMode) {
      setActiveTab('refine');
      setRefineText('');
      setModifyText(currentPrompt);
      return;
    }
    if (!currentPrompt) {
      setSelectedOption(EMPTY_OPTION_VALUE);
      setCustomText('');
      return;
    }
    const match = findMatchingSystemPrompt(
      systemPromptsRef.current,
      currentPrompt
    );
    if (match) {
      setSelectedOption(match.key);
      setCustomText('');
    } else {
      setSelectedOption(CUSTOM_OPTION_VALUE);
      setCustomText(currentPrompt);
    }
  }, [isOpen, prevIsOpen, isRegenerateMode, promptContext?.customPrompt]);

  const handleClose = useCallback(() => {
    setActiveDropdown((prev) => (prev === 'prompt' ? null : prev));
  }, [setActiveDropdown]);

  const handleApply = useCallback(() => {
    let promptValue: string | null;
    let currentSlideContent: string | undefined;
    // Refine output is a one-shot refinement against the existing slide body;
    // the saved prompt should stay pointed at the original instruction so the
    // user can keep refining (or switch to Modify prompt) later.
    let shouldPersistPrompt = true;

    if (isRegenerateMode) {
      const text = activeTab === 'refine' ? refineText : modifyText;
      promptValue = text.trim() || null;
      if (activeTab === 'refine') {
        shouldPersistPrompt = false;
        if (promptValue) {
          currentSlideContent = promptContext?.pageContent || undefined;
        }
      }
    } else if (selectedOption === EMPTY_OPTION_VALUE) {
      promptValue = null;
    } else if (selectedOption === CUSTOM_OPTION_VALUE) {
      promptValue = customText.trim() || null;
    } else {
      // A system prompt key — resolve to its current text from context.
      const match = systemPrompts.find((p) => p.key === selectedOption);
      promptValue = match?.prompt ?? null;
    }

    if (shouldPersistPrompt) {
      promptContext?.onPromptChange(promptValue);
    }

    // In non-template (presentation) mode, trigger AI content generation
    // immediately. In template mode, just save the prompt for deferred generation.
    if (
      !promptContext?.isTemplate &&
      promptValue &&
      designerCtx?.presentationId
    ) {
      promptContext?.startGeneration({
        presentationID: designerCtx.presentationId,
        prompt: promptValue,
        ...(currentSlideContent ? { currentSlideContent } : {}),
      });
    } else {
      promptContext?.notifyPromptApplied();
    }

    handleClose();
  }, [
    isRegenerateMode,
    activeTab,
    refineText,
    modifyText,
    selectedOption,
    customText,
    systemPrompts,
    promptContext,
    designerCtx,
    handleClose,
  ]);

  const promptOptions = useMemo(
    () => [
      ...systemPrompts.map((p) => ({ display: p.displayName, value: p.key })),
      { display: 'Custom', value: CUSTOM_OPTION_VALUE },
    ],
    [systemPrompts]
  );

  if (!promptContext) return null;

  const isCustomSelected = selectedOption === CUSTOM_OPTION_VALUE;
  const selectedSystemPrompt =
    selectedOption && !isCustomSelected
      ? systemPrompts.find((p) => p.key === selectedOption)
      : undefined;
  const generateModeTextValue = isCustomSelected
    ? customText
    : (selectedSystemPrompt?.prompt ?? '');
  const regenerateModeTextValue =
    activeTab === 'refine' ? refineText : modifyText;
  const applyButtonLabel = promptContext.customPrompt ? 'Regenerate' : 'Apply';

  return (
    <Stack sx={{ height: '100%' }} spacing={1.5}>
      {promptContext.isTemplate && (
        <Typography variant="title-xs">
          Use a prompt to generate custom text*
        </Typography>
      )}
      {isRegenerateMode ? (
        <Box sx={{ alignSelf: 'flex-start' }}>
          <ButtonGroup<RegenerateTab>
            label="Regenerate mode"
            hideLabel
            value={activeTab}
            options={REGENERATE_TAB_OPTIONS}
            onChange={(_e, value) => setActiveTab(value)}
          />
        </Box>
      ) : (
        <SelectInput
          label="Select a prompt"
          value={selectedOption}
          options={promptOptions}
          // The parent Popover is elevated to zIndexes.TOOLTIP; the Select
          // menu must sit above it so the dropdown isn't clipped.
          MenuProps={{ sx: { zIndex: zIndexes.TOOLTIP + 1 } }}
          onChange={(e) => {
            const value = e.target.value as string;
            // When switching from a preset to Custom, seed the editable text
            // with the preset's prompt so the user can tweak it.
            if (
              value === CUSTOM_OPTION_VALUE &&
              selectedOption !== CUSTOM_OPTION_VALUE
            ) {
              setCustomText(selectedSystemPrompt?.prompt ?? '');
            }
            setSelectedOption(value);
          }}
        />
      )}
      <TextInput
        label="Prompt"
        hideLabel
        value={
          isRegenerateMode ? regenerateModeTextValue : generateModeTextValue
        }
        onChange={(e) => {
          const value = e.target.value;
          if (isRegenerateMode) {
            if (activeTab === 'refine') {
              setRefineText(value);
            } else {
              setModifyText(value);
            }
          } else {
            setCustomText(value);
          }
        }}
        multiline
        fullWidth
        fillHeight
        disabled={!isRegenerateMode && !isCustomSelected}
        placeholder="Enter your custom prompt..."
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleApply}
          startIcon={Stars01Icon}
        >
          {applyButtonLabel}
        </Button>
      </Stack>
      {promptContext.isTemplate && (
        <Typography variant="body-xs">
          *Generated text will appear in client presentations
        </Typography>
      )}
    </Stack>
  );
}
